import React, { useState } from 'react';
import { Building2, Users, FileText, BarChart2, MessageSquare, Search, Filter, Plus, Sparkles, TrendingUp } from 'lucide-react';
import { OpportunitiesSection } from '../components/marketplace/OpportunitiesSection';
import { SuppliersSection } from '../components/marketplace/SuppliersSection';
import { ResourceHub } from '../components/marketplace/ResourceHub';
import { MarketplaceAnalytics } from '../components/marketplace/MarketplaceAnalytics';
import { CollaborationTools } from '../components/marketplace/CollaborationTools';
import { PostOpportunityModal } from '../components/marketplace/PostOpportunityModal';
import { MarketInsights } from '../components/marketplace/MarketInsights';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

export function Marketplace() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('tab') || 'opportunities';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);

  const tabs = [
    { id: 'opportunities', name: 'Opportunities', icon: Building2, count: 156 },
    { id: 'partners', name: 'Partners', icon: Users, count: 45 },
    { id: 'resources', name: 'Resources', icon: FileText, count: 24 },
    { id: 'collaboration', name: 'Collaboration', icon: MessageSquare, count: 8 },
    { id: 'analytics', name: 'Analytics', icon: BarChart2 },
  ];

  const quickStats = [
    { label: 'Active Opportunities', value: '156', change: '+12%', icon: Building2 },
    { label: 'Potential Partners', value: '45', change: '+8%', icon: Users },
    { label: 'Success Rate', value: '68%', change: '+4.2%', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen space-y-6">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 to-purple-900 px-6 py-16">
        <div className="absolute inset-0 bg-grid-white/[0.1] bg-[size:16px_16px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent" />
        
        {/* Decorative Elements */}
        <div className="absolute -left-20 -top-20 h-[500px] w-[500px] rounded-full bg-purple-400/30 blur-[100px]" />
        <div className="absolute -right-20 -bottom-20 h-[500px] w-[500px] rounded-full bg-purple-500/30 blur-[100px]" />
        
        <div className="relative mx-auto max-w-7xl">
          <div className="flex flex-col space-y-6">
            {/* Header Content */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white sm:text-4xl">Marketplace</h1>
                <p className="mt-2 text-lg text-purple-100">
                  Discover opportunities, find partners, and access resources
                </p>
              </div>
              <button 
                onClick={() => setShowPostModal(true)}
                className="inline-flex items-center rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm transition-all hover:bg-white/20"
              >
                <Plus className="mr-2 h-4 w-4" />
                Post Opportunity
              </button>
            </div>

            {/* Search Bar */}
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-purple-300" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search opportunities, partners, or resources..."
                  className="w-full rounded-lg border-0 bg-white/10 px-12 py-3 text-white placeholder-purple-200 backdrop-blur-sm transition-colors focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <kbd className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 rounded-md border border-purple-400/20 bg-purple-400/10 px-2 py-1 text-xs font-medium text-purple-200">
                  ⌘K
                </kbd>
              </div>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center rounded-lg bg-white/10 px-4 py-3 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                <Filter className="mr-2 h-4 w-4" />
                Filters
              </button>
            </div>

            {/* Quick Stats */}
            <div className="mt-8 grid gap-6 sm:grid-cols-3">
              {quickStats.map((stat) => (
                <div
                  key={stat.label}
                  className="group relative overflow-hidden rounded-lg bg-white/10 p-6 backdrop-blur-sm transition-all hover:bg-white/20"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-200">{stat.label}</p>
                      <p className="mt-2 flex items-baseline">
                        <span className="text-3xl font-bold text-white">{stat.value}</span>
                        <span className="ml-2 text-sm font-medium text-green-300">{stat.change}</span>
                      </p>
                    </div>
                    <div className="rounded-full bg-white/20 p-3">
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-6">
        {/* Tabs */}
        <div className="flex space-x-1 rounded-lg bg-white p-1 shadow-sm">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-1 items-center justify-center space-x-2 rounded-md px-3 py-2.5 text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white shadow'
                  : 'text-gray-600 hover:bg-purple-50 hover:text-purple-600'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.name}</span>
              {tab.count && (
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  activeTab === tab.id
                    ? 'bg-white/20 text-white'
                    : 'bg-purple-100 text-purple-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-900/5">
            {activeTab === 'opportunities' && <OpportunitiesSection />}
            {activeTab === 'partners' && <SuppliersSection />}
            {activeTab === 'resources' && <ResourceHub />}
            {activeTab === 'collaboration' && <CollaborationTools />}
            {activeTab === 'analytics' && <MarketplaceAnalytics />}
          </div>
        </div>

        {/* Market Insights */}
        <div className="mt-8">
          <MarketInsights />
        </div>
      </div>

      {/* Post Opportunity Modal */}
      <PostOpportunityModal 
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
      />

      {/* Decorative Elements */}
      <div className="pointer-events-none fixed inset-0 z-30 overflow-hidden">
        <div className="absolute -left-1/2 top-0 h-[1000px] w-[1000px] rounded-full bg-purple-500/5 blur-3xl"></div>
        <div className="absolute -right-1/2 bottom-0 h-[1000px] w-[1000px] rounded-full bg-purple-500/5 blur-3xl"></div>
      </div>
    </div>
  );
}